import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Brain, 
  BookOpen, 
  Target, 
  Clock,
  User,
  TrendingUp,
  Lightbulb,
  Play
} from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { 
  ComprehensionAssessmentModal,
  useStudentComprehensionLevel 
} from '@/components/assessment';
import { 
  usePersonalizedContent, 
  getPersonalizedExplanation,
  SAMPLE_EXPLANATIONS 
} from '@/utils/content-personalizer';
import { useAuth } from '@/components/auth/AuthProvider';
import { comprehensionService } from '@/services/comprehension-service';

export function ComprehensionAssessmentPage() {
  const { user } = useAuth();
  const [showAssessmentModal, setShowAssessmentModal] = useState(false);
  const [hasActiveSession, setHasActiveSession] = useState(false);
  const { 
    level, 
    score, 
    hasAssessment, 
    isLoading,
    personalizeExplanation,
    getVocabularyLevel,
    isBasicLevel,
    isIntermediateLevel,
    isAdvancedLevel,
    isExpertLevel
  } = usePersonalizedContent();

  // Check for active session
  useEffect(() => {
    if (user?.id) {
      const activeSession = comprehensionService.hasActiveSession(user.id);
      setHasActiveSession(activeSession);
    }
  }, [user?.id]);

  const handleTakeAssessment = () => {
    setShowAssessmentModal(true);
  };

  const handleModalClose = () => {
    setShowAssessmentModal(false);
    // Recheck session state after modal closes
    if (user?.id) {
      const activeSession = comprehensionService.hasActiveSession(user.id);
      setHasActiveSession(activeSession);
    }
  };

  const levelConfig = {
    1: { name: 'Basic', color: 'bg-green-100 text-green-800 border-green-200', icon: '🌱' },
    2: { name: 'Intermediate', color: 'bg-blue-100 text-blue-800 border-blue-200', icon: '🌿' },
    3: { name: 'Advanced', color: 'bg-purple-100 text-purple-800 border-purple-200', icon: '🌳' },
    4: { name: 'Expert', color: 'bg-orange-100 text-orange-800 border-orange-200', icon: '🏆' }
  };

  const currentLevelConfig = levelConfig[level as keyof typeof levelConfig] || levelConfig[2];

  if (isLoading) {
    return (
      <div className="container mx-auto p-6 max-w-6xl">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          <span className="ml-3">Loading assessment data...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 max-w-6xl space-y-6">
      {/* Header */}
      <div className="text-center space-y-4">
        <div className="flex items-center justify-center space-x-3">
          <Brain className="h-8 w-8 text-primary" />
          <h1 className="text-3xl font-bold">Comprehension Assessment</h1>
        </div>
        <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
          Take the assessment to get personalized learning experiences based on your comprehension level
        </p>
      </div>

      {/* Current Level Status */}
      <Card className="border-2 border-primary/20">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="p-3 bg-primary/10 rounded-lg">
                <User className="h-6 w-6 text-primary" />
              </div>
              <div>
                <CardTitle>Your Learning Profile</CardTitle>
                <p className="text-sm text-muted-foreground">
                  {user?.email || 'Student'} • Current comprehension level
                </p>
              </div>
            </div>
            {!hasAssessment && (
              <Button onClick={handleTakeAssessment} size="sm">
                <Play className="h-4 w-4 mr-2" />
                {hasActiveSession ? 'Resume Assessment' : 'Take Assessment'}
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {hasAssessment ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <div className="flex items-center space-x-2">
                  <span className="text-2xl">{currentLevelConfig.icon}</span>
                  <Badge className={currentLevelConfig.color}>
                    Level {level}: {currentLevelConfig.name}
                  </Badge>
                </div>
                <p className="text-sm text-muted-foreground">
                  Comprehension Level
                </p>
              </div>
              
              <div className="space-y-2">
                <div className="flex items-center space-x-2">
                  <TrendingUp className="h-5 w-5 text-blue-600" />
                  <span className="text-xl font-semibold">{score.toFixed(1)}/4.0</span>
                </div>
                <p className="text-sm text-muted-foreground">
                  Assessment Score
                </p>
              </div>
              
              <div className="space-y-2">
                <div className="flex items-center space-x-2">
                  <Target className="h-5 w-5 text-green-600" />
                  <span className="text-sm font-medium capitalize">{getVocabularyLevel()}</span>
                </div>
                <p className="text-sm text-muted-foreground">
                  Vocabulary Level
                </p>
              </div>
            </div>
          ) : (
            <Alert>
              <Lightbulb className="h-4 w-4" />
              <AlertDescription>
                Take the comprehension assessment to get personalized explanations tailored to your learning level.
                This helps our system explain concepts in a way that matches your understanding.
              </AlertDescription>
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Demonstration */}
      <Tabs defaultValue="examples" className="space-y-4">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="examples">Personalized Content</TabsTrigger>
          <TabsTrigger value="comparison">Level Examples</TabsTrigger>
          <TabsTrigger value="assessment">How It Works</TabsTrigger>
        </TabsList>

        <TabsContent value="examples" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Photosynthesis Example */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <BookOpen className="h-5 w-5 text-green-600" />
                  <span>Photosynthesis</span>
                  <Badge variant="outline">Science</Badge>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex items-center space-x-2">
                    <span className="text-sm font-medium">Your Level:</span>
                    <Badge className={currentLevelConfig.color}>
                      {currentLevelConfig.name}
                    </Badge>
                  </div>
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <p className="text-sm leading-relaxed">
                      {personalizeExplanation(SAMPLE_EXPLANATIONS.photosynthesis)}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Quadratic Equation Example */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <BookOpen className="h-5 w-5 text-blue-600" />
                  <span>Quadratic Equations</span>
                  <Badge variant="outline">Mathematics</Badge>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex items-center space-x-2">
                    <span className="text-sm font-medium">Your Level:</span>
                    <Badge className={currentLevelConfig.color}>
                      {currentLevelConfig.name}
                    </Badge>
                  </div>
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <p className="text-sm leading-relaxed">
                      {personalizeExplanation(SAMPLE_EXPLANATIONS.quadraticEquation)}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="comparison" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>How Explanations Change by Level</CardTitle>
              <p className="text-sm text-muted-foreground">
                See how the same concept is explained differently for each comprehension level
              </p>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                {Object.entries(levelConfig).map(([levelNum, config]) => (
                  <div key={levelNum} className="space-y-3">
                    <div className="flex items-center space-x-3">
                      <span className="text-xl">{config.icon}</span>
                      <Badge className={config.color}>
                        Level {levelNum}: {config.name}
                      </Badge>
                      {parseInt(levelNum) === level && (
                        <Badge variant="secondary">Your Current Level</Badge>
                      )}
                    </div>
                    <div className="p-4 bg-gray-50 rounded-lg border-l-4 border-gray-300">
                      <p className="text-sm leading-relaxed">
                        {getPersonalizedExplanation('photosynthesis', parseInt(levelNum))}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="assessment" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <Brain className="h-5 w-5 text-primary" />
                  <span>How It Works</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-3">
                  <div className="flex items-start space-x-3">
                    <Badge variant="secondary" className="min-w-fit">1</Badge>
                    <p className="text-sm">Students answer 5 multiple-choice questions</p>
                  </div>
                  <div className="flex items-start space-x-3">
                    <Badge variant="secondary" className="min-w-fit">2</Badge>
                    <p className="text-sm">Each answer has a comprehension level (1-4)</p>
                  </div>
                  <div className="flex items-start space-x-3">
                    <Badge variant="secondary" className="min-w-fit">3</Badge>
                    <p className="text-sm">System calculates average comprehension level</p>
                  </div>
                  <div className="flex items-start space-x-3">
                    <Badge variant="secondary" className="min-w-fit">4</Badge>
                    <p className="text-sm">Content is personalized to that level</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <Clock className="h-5 w-5 text-blue-600" />
                  <span>Assessment Schedule</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-3">
                  <div className="p-3 bg-blue-50 rounded-lg">
                    <h4 className="font-medium text-blue-800">Initial Assessment</h4>
                    <p className="text-sm text-blue-600">
                      First time students use the system
                    </p>
                  </div>
                  <div className="p-3 bg-orange-50 rounded-lg">
                    <h4 className="font-medium text-orange-800">Re-assessment</h4>
                    <p className="text-sm text-orange-600">
                      Every 6 months to update comprehension level
                    </p>
                  </div>
                  <div className="p-3 bg-green-50 rounded-lg">
                    <h4 className="font-medium text-green-800">Privacy</h4>
                    <p className="text-sm text-green-600">
                      Scores are private and used only for personalization
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {!hasAssessment && (
            <Card className="border-primary/20 bg-primary/5">
              <CardContent className="p-6 text-center">
                <Brain className="h-12 w-12 text-primary mx-auto mb-4" />
                <h3 className="text-xl font-semibold mb-2">Ready to Get Started?</h3>
                <p className="text-muted-foreground mb-4">
                  Take the comprehension assessment to unlock personalized learning experiences
                </p>
                <Button onClick={handleTakeAssessment} size="lg">
                  <Play className="h-5 w-5 mr-2" />
                  {hasActiveSession ? 'Resume Assessment' : 'Start Assessment'}
                </Button>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* Assessment Modal */}
      <ComprehensionAssessmentModal 
        open={showAssessmentModal} 
        onOpenChange={handleModalClose} 
      />
    </div>
  );
}
